import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentOrgId } from "@/hooks/auth";
import { useToast } from "@/hooks/use-toast";
import { sanitizeStorageFileName } from "@/lib/fileUtils";
import type { AttachmentValue } from "@/types/fieldRegistry";

const BUCKET = "custom-field-files";
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB
const SIGNED_URL_TTL_SECONDS = 3600;

/**
 * Upload de arquivos para campos personalizados do tipo 'attachment'.
 * Não usa tabela: os metadados (AttachmentValue) são retornados para o caller
 * gravar no JSONB custom_data da entidade. Os bytes ficam no bucket privado
 * 'custom-field-files', com caminho por organização.
 */
export function useCustomFieldFiles() {
  const { orgId } = useCurrentOrgId();
  const { toast } = useToast();
  const [isUploading, setIsUploading] = useState(false);

  /** URL assinada para leitura (bucket privado). Válida por 1 hora. */
  const getFileUrl = async (filePath: string): Promise<string | null> => {
    const { data, error } = await supabase.storage
      .from(BUCKET)
      .createSignedUrl(filePath, SIGNED_URL_TTL_SECONDS);

    if (error) {
      console.error("Error creating signed URL:", error);
      toast({
        title: "Erro ao abrir arquivo",
        description: error.message || "Não foi possível gerar o link do arquivo.",
        variant: "destructive",
      });
      return null;
    }

    return data.signedUrl;
  };

  /** Faz upload de 1+ arquivos e retorna os metadados dos que subiram com sucesso. */
  const uploadFiles = async (files: File[]): Promise<AttachmentValue[]> => {
    if (!orgId) {
      toast({
        title: "Organização não encontrada",
        description: "Não foi possível identificar a organização para o upload.",
        variant: "destructive",
      });
      return [];
    }

    setIsUploading(true);
    const uploaded: AttachmentValue[] = [];
    try {
      for (const file of files) {
        if (file.size > MAX_FILE_SIZE) {
          toast({
            title: "Arquivo muito grande",
            description: `"${file.name}" excede o tamanho máximo de 50MB.`,
            variant: "destructive",
          });
          continue;
        }

        const filePath = `${orgId}/custom-field-files/${Date.now()}_${sanitizeStorageFileName(file.name)}`;
        const { error: uploadError } = await supabase.storage
          .from(BUCKET)
          .upload(filePath, file);

        if (uploadError) {
          console.error("Error uploading custom field file:", uploadError);
          toast({
            title: "Erro ao enviar arquivo",
            description: `${file.name}: ${uploadError.message}`,
            variant: "destructive",
          });
          continue;
        }

        uploaded.push({
          path: filePath,
          name: file.name,
          size: file.size,
          type: file.type,
          uploaded_at: new Date().toISOString(),
        });
      }

      if (uploaded.length > 0) {
        toast({
          title: uploaded.length === 1 ? "Arquivo anexado" : "Arquivos anexados",
          description:
            uploaded.length === 1
              ? `${uploaded[0].name} foi anexado com sucesso.`
              : `${uploaded.length} arquivos foram anexados com sucesso.`,
        });
      }
    } finally {
      setIsUploading(false);
    }

    return uploaded;
  };

  /** Remove um arquivo do storage (best-effort). */
  const removeFromStorage = async (filePath: string): Promise<void> => {
    const { error } = await supabase.storage.from(BUCKET).remove([filePath]);
    if (error) {
      console.error("Error removing custom field file:", error);
    }
  };

  return { isUploading, uploadFiles, getFileUrl, removeFromStorage };
}
